import { useEffect, useState, useCallback, useMemo } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "../components/ui/pagination";
import {
  Search,
  Package,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  ArrowUpDown,
  Warehouse,
  TrendingDown,
  ShoppingCart,
  Monitor,
  Building2,
} from "lucide-react";
import { cn } from "../lib/utils";
import { Link } from "react-router-dom";
import {
  server_post_data,
  get_department_with_categories,
  get_view_stock_inventory,
  get_view_units,
} from "../ServiceConnection/serviceconnection";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const num = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

/** Chhoti value pe zyada decimals — 60 g in KG = 0.06, "0.00" nahi */
const fmtQty = (v) => {
  const n = num(v);
  if (n === 0) return "0";
  const abs = Math.abs(n);
  let d = 2;
  if (abs < 0.001) d = 6;
  else if (abs < 0.01) d = 5;
  else if (abs < 1) d = 4;
  else if (abs < 100) d = 3;
  return String(parseFloat(n.toFixed(d)));
};

const money = (v) =>
  `₹${num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function StockInventory() {
  const [searchQuery, setSearchQuery] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [sortField, setSortField] = useState("ingredient_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(true);

  const [ingredients, setIngredients] = useState([]);
  const [units, setUnits] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  /* ---------------- unit helpers ---------------- */

  const unitMap = useMemo(() => {
    const m = {};
    (units || []).forEach((u) => {
      m[String(u.primary_id)] = u;
    });
    return m;
  }, [units]);

  // Base unit ka conversion_to_base_unit = 1 hota hai (0 nahi)
  const convFactor = useCallback(
    (unitId) => {
      const f = num(unitMap[String(unitId)]?.conversion_to_base_unit, 1);
      return f > 0 ? f : 1;
    },
    [unitMap],
  );

  const unitLabel = useCallback(
    (unitId) => {
      const u = unitMap[String(unitId)];
      if (!u) return "—";
      const short = u.short_code_name || u.short_code;
      return short ? `${u.unit_name} (${short})` : u.unit_name;
    },
    [unitMap],
  );

  const unitShort = useCallback(
    (unitId) => {
      const u = unitMap[String(unitId)];
      return u?.short_code_name || u?.short_code || u?.unit_name || "";
    },
    [unitMap],
  );

  /**
   * IMPORTANT
   * balance_stock backend se DISPLAY unit me aata hai (KG),
   * lekin rate PER BASE UNIT hota hai (per gram).
   *
   *   value = balance_display x conversion_to_base_unit x rate_per_base
   *
   * Purana code seedha balance x rate karta tha — 1000x kam aata tha.
   */
  const rowValue = useCallback(
    (item) => {
      const balDisplay = num(item.balance_stock);
      const balBase = balDisplay * convFactor(item.default_unit_id);

      let ratePerBase = num(item.avg_landed_price);
      if (ratePerBase <= 0) ratePerBase = num(item.avg_price);
      if (ratePerBase <= 0) ratePerBase = num(item.last_price);

      const ratePerDisplay = ratePerBase * convFactor(item.default_unit_id);

      return {
        balDisplay,
        balBase,
        ratePerBase,
        ratePerDisplay,
        value: balBase * ratePerBase,
      };
    },
    [convFactor],
  );

  /* ---------------- data ---------------- */

  const fetchUnits = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      // get_view_units me conversion_to_base_unit aata hai,
      // stock API ke units_data me nahi — isliye alag call
      const res = await server_post_data(get_view_units, fd);
      if (!res.data.error) setUnits(res.data.message || []);
    } catch (e) {
      console.error("units:", e);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_department_with_categories, fd);
      if (!res.data.error && res.data.message?.data) {
        const dynamicTabs = res.data.message.data.map((dep) => ({
          key: `dept_${dep.department_id}`, // id se key — naam se collision hota tha
          label: dep.department_name,
          primary_id: String(dep.department_id),
          icon: Building2,
        }));

        setTabs([
          { key: "all", label: "All", primary_id: "0", icon: Monitor },
          ...dynamicTabs,
        ]);
      }
    } catch (e) {
      console.error("departments:", e);
    }
  }, []);

  const fetchIngredients = useCallback(async (selected_id) => {
    setLoading(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("selected_id", selected_id);
    fd.append("flag", "0");

    try {
      const res = await server_post_data(get_view_stock_inventory, fd);
      if (!res.data.error && res.data.message?.ingredients_data) {
        setIngredients(res.data.message.ingredients_data);
      } else {
        setIngredients([]);
      }
    } catch (e) {
      console.error("ingredients:", e);
      setIngredients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnits();
    fetchDepartments();
  }, [fetchUnits, fetchDepartments]);

  // Tab change -> us department ka stock
  useEffect(() => {
    if (tabs.length === 0) return;
    const selected = tabs.find((t) => t.key === activeTab);
    if (selected) fetchIngredients(selected.primary_id);
  }, [activeTab, tabs, fetchIngredients]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, stockFilter, activeTab]);

  /* ---------------- filter / sort ---------------- */

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const isAllTab = activeTab === "all";

  const filteredIngredients = useMemo(() => {
    let list = [...ingredients];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (item) =>
          (item.ingredient_name || "").toLowerCase().includes(q) ||
          (item.ingredient_alter_name || "").toLowerCase().includes(q) ||
          String(item.primary_id).includes(q),
      );
    }

    // Comparison numbers pe — pehle string compare ho raha tha
    if (stockFilter === "in-stock") {
      list = list.filter(
        (i) => num(i.balance_stock) > num(i.minimum_stock_alert),
      );
    } else if (stockFilter === "low-stock") {
      list = list.filter(
        (i) =>
          num(i.balance_stock) > 0 &&
          num(i.balance_stock) <= num(i.minimum_stock_alert),
      );
    } else if (stockFilter === "out-of-stock") {
      list = list.filter((i) => num(i.balance_stock) <= 0);
    }

    const textFields = [
      "ingredient_name",
      "dcategory_name",
      "department_name",
    ];

    list.sort((a, b) => {
      let valA;
      let valB;

      if (sortField === "stock_value") {
        valA = rowValue(a).value;
        valB = rowValue(b).value;
      } else if (textFields.includes(sortField)) {
        valA = (a[sortField] || "").toString().toLowerCase();
        valB = (b[sortField] || "").toString().toLowerCase();
      } else {
        valA = num(a[sortField]);
        valB = num(b[sortField]);
      }

      const cmp = valA > valB ? 1 : valA < valB ? -1 : 0;
      return sortOrder === "asc" ? cmp : -cmp;
    });

    return list;
  }, [ingredients, searchQuery, stockFilter, sortField, sortOrder, rowValue]);

  const totalItems = filteredIngredients.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredIngredients.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push("ellipsis", totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, "ellipsis");
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, "ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis", totalPages);
    }
    return pages;
  };

  /* ---------------- stats ---------------- */

  const stats = useMemo(() => {
    const total = ingredients.length;
    const inStock = ingredients.filter(
      (i) => num(i.balance_stock) > num(i.minimum_stock_alert),
    ).length;
    const lowStock = ingredients.filter(
      (i) =>
        num(i.balance_stock) > 0 &&
        num(i.balance_stock) <= num(i.minimum_stock_alert),
    ).length;
    const outOfStock = ingredients.filter(
      (i) => num(i.balance_stock) <= 0,
    ).length;

    const totalValue = ingredients.reduce(
      (sum, i) => sum + rowValue(i).value,
      0,
    );

    return { total, inStock, lowStock, outOfStock, totalValue };
  }, [ingredients, rowValue]);

  /* ---------------- export ---------------- */

  const handleExport = () => {
    const rows = [
      [
        "Item ID",
        "Item Name",
        "Category",
        "Department",
        "Unit",
        "Current Stock",
        "Min Stock",
        "Avg Rate",
        "Stock Value",
        "Status",
      ],
    ];

    filteredIngredients.forEach((item) => {
      const v = rowValue(item);
      rows.push([
        `LI${String(item.primary_id).padStart(4, "0")}`,
        item.ingredient_name || "",
        item.dcategory_name || "",
        item.department_name || "",
        unitShort(item.default_unit_id),
        fmtQty(v.balDisplay),
        fmtQty(item.minimum_stock_alert),
        v.ratePerDisplay.toFixed(2),
        v.value.toFixed(2),
        num(item.balance_stock) <= 0
          ? "Out of Stock"
          : num(item.balance_stock) <= num(item.minimum_stock_alert)
            ? "Low Stock"
            : "In Stock",
      ]);
    });

    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `stock_inventory_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const getStockBadge = (item) => {
    const bal = num(item.balance_stock);
    const min = num(item.minimum_stock_alert);
    if (bal <= 0)
      return <Badge className="bg-red-100 text-red-700">Out of Stock</Badge>;
    if (bal <= min)
      return <Badge className="bg-amber-100 text-amber-700">Low Stock</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-700">In Stock</Badge>;
  };

  const colCount = isAllTab ? 9 : 10;

  /* ---------------- render ---------------- */

  return (
    <MainLayout>
      <Header
        title="Stock & Inventory"
        subtitle="Monitor current stock levels and manage inventory"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="inline-flex h-auto p-0 bg-transparent w-full justify-start overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
                className="relative h-auto py-3 px-4 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:bg-muted/50 transition-colors whitespace-nowrap"
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </div>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Warehouse className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Stock</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {stats.inStock}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Low Stock</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {stats.lowStock}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Out of Stock</p>
                  <p className="text-2xl font-bold text-red-600">
                    {stats.outOfStock}
                  </p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Stock Value</p>
                  <p className="text-2xl font-bold">
                    {money(stats.totalValue)}
                  </p>
                </div>
                <Package className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Low stock alert */}
        {stats.lowStock + stats.outOfStock > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TrendingDown className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-800">
                    {stats.lowStock + stats.outOfStock} items need restocking
                  </span>
                </div>
                <Link to="/create-indent">
                  <Button size="sm" className="gap-2">
                    <ShoppingCart className="w-4 h-4" />
                    Raise Indent
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters + table */}
        <Card>
          <CardContent className="mt-5">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search items by name, ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Select value={stockFilter} onValueChange={setStockFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Stock Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="in-stock">In Stock</SelectItem>
                  <SelectItem value="low-stock">Low Stock</SelectItem>
                  <SelectItem value="out-of-stock">Out of Stock</SelectItem>
                </SelectContent>
              </Select>

              <div className="md:col-start-5 flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleExport}
                  disabled={totalItems === 0}
                  className="gap-2 w-full md:w-auto"
                >
                  <Download className="w-4 h-4" />
                  Export
                </Button>
              </div>
            </div>

            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-muted-foreground">
                      Item ID
                    </TableHead>
                    <TableHead className="text-muted-foreground">
                      <button
                        onClick={() => toggleSort("ingredient_name")}
                        className="flex items-center gap-1 hover:text-foreground"
                      >
                        Item Name <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </TableHead>

                    {/* Pehle dono column ka header "Department" tha —
                        ye actually category hai */}
                    <TableHead className="text-muted-foreground">
                      <button
                        onClick={() => toggleSort("dcategory_name")}
                        className="flex items-center gap-1 hover:text-foreground"
                      >
                        Category <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </TableHead>

                    {!isAllTab && (
                      <TableHead className="text-muted-foreground">
                        Department
                      </TableHead>
                    )}

                    <TableHead className="text-center text-muted-foreground">
                      Unit
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      <button
                        onClick={() => toggleSort("balance_stock")}
                        className="flex items-center gap-1 hover:text-foreground mx-auto"
                      >
                        Current Stock <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      <button
                        onClick={() => toggleSort("minimum_stock_alert")}
                        className="flex items-center gap-1 hover:text-foreground mx-auto"
                      >
                        Min Stock <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right text-muted-foreground">
                      Avg Rate
                    </TableHead>
                    <TableHead className="text-right text-muted-foreground">
                      <button
                        onClick={() => toggleSort("stock_value")}
                        className="flex items-center gap-1 hover:text-foreground ml-auto"
                      >
                        Stock Value <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </TableHead>
                    <TableHead className="text-center text-muted-foreground">
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={colCount}
                        className="text-center py-12 text-muted-foreground"
                      >
                        Loading inventory...
                      </TableCell>
                    </TableRow>
                  ) : currentItems.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={colCount}
                        className="text-center py-12 text-muted-foreground"
                      >
                        {ingredients.length === 0
                          ? "No stock in this department yet"
                          : "No items match this search"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentItems.map((item) => {
                      const v = rowValue(item);
                      const bal = num(item.balance_stock);
                      const min = num(item.minimum_stock_alert);
                      const short = unitShort(item.default_unit_id);

                      return (
                        <TableRow
                          key={`${item.primary_id}_${item.department_id || 0}`}
                          className={cn(
                            "hover:bg-muted/30 transition-colors",
                            bal <= 0 && "bg-red-50/50",
                            bal > 0 && bal <= min && "bg-amber-50/50",
                          )}
                        >
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            LI{String(item.primary_id).padStart(4, "0")}
                          </TableCell>

                          <TableCell className="font-medium">
                            {item.ingredient_name}
                            {item.ingredient_alter_name &&
                              item.ingredient_alter_name.trim().toLowerCase() !==
                                (item.ingredient_name || "")
                                  .trim()
                                  .toLowerCase() && (
                                <p className="text-[11px] text-muted-foreground">
                                  ({item.ingredient_alter_name})
                                </p>
                              )}
                          </TableCell>

                          <TableCell>
                            <Badge
                              variant="outline"
                              className="rounded-full px-3 py-1 text-xs"
                            >
                              {item.dcategory_name || "—"}
                            </Badge>
                          </TableCell>

                          {!isAllTab && (
                            <TableCell>
                              <Badge
                                variant="secondary"
                                className="rounded-full px-3 py-1 text-xs"
                              >
                                {item.department_name || "—"}
                              </Badge>
                            </TableCell>
                          )}

                          <TableCell className="text-center text-muted-foreground">
                            {unitLabel(item.default_unit_id)}
                          </TableCell>

                          <TableCell className="text-center font-semibold">
                            {fmtQty(bal)}{" "}
                            <span className="text-[11px] text-muted-foreground font-normal">
                              {short}
                            </span>
                          </TableCell>

                          <TableCell className="text-center text-muted-foreground">
                            {min > 0 ? `${fmtQty(min)} ${short}` : "—"}
                          </TableCell>

                          <TableCell className="text-right">
                            {v.ratePerDisplay > 0 ? (
                              <>
                                {money(v.ratePerDisplay)}
                                <p className="text-[11px] text-muted-foreground">
                                  per {short}
                                </p>
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>

                          <TableCell className="text-right font-medium">
                            {v.value > 0 ? money(v.value) : "—"}
                          </TableCell>

                          <TableCell className="text-center">
                            {getStockBadge(item)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalItems > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Show</span>
                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={(value) => {
                      setItemsPerPage(parseInt(value, 10));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">
                    of {totalItems} items
                  </span>
                </div>

                {totalPages > 1 && (
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1)
                              setCurrentPage((p) => p - 1);
                          }}
                          className={cn(
                            "cursor-pointer",
                            currentPage === 1 &&
                              "pointer-events-none opacity-50",
                          )}
                        />
                      </PaginationItem>

                      {getPageNumbers().map((pageNum, idx) =>
                        pageNum === "ellipsis" ? (
                          <PaginationItem key={`ellipsis-${idx}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentPage(pageNum);
                              }}
                              isActive={currentPage === pageNum}
                              className={cn(
                                "cursor-pointer",
                                currentPage === pageNum &&
                                  "border-primary bg-primary/10 text-primary hover:bg-primary/20",
                              )}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        ),
                      )}

                      <PaginationItem>
                        <PaginationNext
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages)
                              setCurrentPage((p) => p + 1);
                          }}
                          className={cn(
                            "cursor-pointer",
                            currentPage === totalPages &&
                              "pointer-events-none opacity-50",
                          )}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-4">
              Stock value uses each ingredient's average landed cost. Quantities
              are shown in the ingredient's own unit.
            </p>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}